// The eager half of the assistant panel: a floating button and nothing else.
//
// The panel body (assistant-ui runtime, thread, renderers, voice notes) is a
// lazy chunk loaded on first open — the old /chat page was a lazy route, and
// moving chat into the shell must not move all of that into every operator
// page load. Once opened, the panel stays MOUNTED for the rest of the session
// (hidden when closed) so the conversation survives close/reopen and
// navigation.

import { Suspense, lazy, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { MessageCircle } from 'lucide-react'
import { useAssistantPanel } from './AssistantPanelProvider'
import { useRole } from '@/hooks/useRole'
import { useBranding } from '@/contexts/BrandingContext'

const AssistantPanel = lazy(() => import('./AssistantPanel'))

/**
 * The messages screens draw their own composer, whose send button sits in the
 * very corner this button floats in — on a phone the bubble covers it outright.
 * The panel itself is untouched: an already-open conversation survives the
 * navigation, only the floating entry point stands down.
 */
export function hidesAssistantLauncher(pathname: string): boolean {
  return /^\/(inbox|conversation)(\/|$)/.test(pathname)
}

export function AssistantLauncher() {
  const { isOpen, open } = useAssistantPanel()
  const { userRole } = useRole()
  const branding = useBranding()
  const { pathname } = useLocation()

  // Once opened, keep the panel mounted forever — closing hides it, it does
  // not unmount, or the conversation would reset on every close.
  const [everOpened, setEverOpened] = useState(isOpen)
  useEffect(() => {
    if (isOpen) setEverOpened(true)
  }, [isOpen])

  // Operator surface only — mentions search the whole customer base and
  // booking rows deep-link into records.
  if (userRole !== 'owner' && userRole !== 'staff') return null

  return (
    <>
      {!isOpen && !hidesAssistantLauncher(pathname) && (
        <button
          type="button"
          onClick={open}
          aria-label="Open innoAI assistant"
          className="fixed right-5 bottom-5 z-30 flex size-13 items-center justify-center rounded-full text-white shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: branding.primaryColor }}
        >
          <MessageCircle className="size-6" aria-hidden />
        </button>
      )}
      {everOpened && (
        <Suspense fallback={null}>
          <AssistantPanel />
        </Suspense>
      )}
    </>
  )
}
